import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import TableSortLabel from '@mui/material/TableSortLabel'
import { formatMoney, formatPercentage } from '../../packages/utils/numberFormatters'
import { getLevelName, formatFeeLevel } from '../issues/utils'
import { Fee } from '../../types/FeeEntity'
import { InvestorBase } from '../../types/InvestorEntity'
import { InvestorNameCell } from '../../components/entities/InvestorNameCell'

type LevelEntry = {
    feeLevel: number
    amountInPercentage: number
    amountInCash: number
    withoutRights?: boolean | null
}

type InvestorWithLevels = {
    id: string
    investorId: string
    name: string
    leo: boolean
    putOption: boolean
    totalAmountInCash: number
    totalAmountInPercentage: number
    levels: LevelEntry[]
}

const isWithoutRightsFlag = (v: boolean | 0 | 1 | null | undefined) => v === true || v === 1

const isPutOptionFlag = (v: boolean | 0 | 1 | null | undefined) => v === true || v === 1

type SortableKeys = keyof Pick<InvestorWithLevels, 'name' | 'totalAmountInCash'> | 'level_${number}' | 'default'

/**
 * Works with the investors (issue.investors) array and returns a list of objects with the following properties:
 * - name: the name of the investor
 * - leo: flag to indicate if the investor is a Leo
 * - totalAmountInCash: the total amount of the issue the investor is responsible for in cash
 * - totalAmountInPercentage: the total amount of the issue the investor is responsible for in percentage
 * - levels: list of levels the investor is in, where each level is an object with the following properties:
 *  - feeLevel: the level number, starting from 0 (0 is Underwriter)
 *  - amountInPercentage: the amount of the issue the investor is responsible for in percentage
 *  - amountInCash: the amount of the issue the investor is responsible for in cash
 */
function getLevelColumnCount(maxFeeLevel: number, splitFeeLevelZeroColumn: boolean) {
    return splitFeeLevelZeroColumn ? maxFeeLevel + 2 : maxFeeLevel + 1
}

function getLevelForDisplayColumn(
    levels: LevelEntry[],
    displayIdx: number,
    splitFeeLevelZeroColumn: boolean,
): LevelEntry | undefined {
    if (!splitFeeLevelZeroColumn) {
        return levels.find((l) => l.feeLevel === displayIdx)
    }
    if (displayIdx === 0) {
        return levels.find((l) => l.feeLevel === 0 && !isWithoutRightsFlag(l.withoutRights))
    }
    if (displayIdx === 1) {
        return levels.find((l) => l.feeLevel === 0 && isWithoutRightsFlag(l.withoutRights))
    }
    return levels.find((l) => l.feeLevel === displayIdx - 1)
}

function mapInvestorsToNamesAndLevels({
    investors,
    totalOfferingSize,
}: {
    investors: InvestorBase[]
    totalOfferingSize: number
}) {
    // Each investor is an object with the following properties:
    // - name: the name of the investor
    // - leo: flag to indicate if the investor is a Leo
    // - feeLevel: the level number, starting from 0 (0 is Underwriter)
    // - amountInPercentage: the amount of the issue the investor is responsible for in percentage
    // - amountInCash: the amount of the issue the investor is responsible for in cash
    // Each investor can be in multiple levels, so may appear multiple times in the list
    let investorsNotSellers = investors
    // remove sellers from the list of investors
    investorsNotSellers = investorsNotSellers.filter((investor) => !investor.isSeller)
    const investorsWithLevels = investorsNotSellers.reduce(
        (acc, investor) => {
            const { id, investorId, name, leo, feeLevel, putOption } = investor
            // the amounts should either be set or calculated from the other amount
            const amountInPercentage =
                investor.amountInPercentage !== null &&
                investor.amountInPercentage !== undefined &&
                isFinite(investor.amountInPercentage)
                    ? investor.amountInPercentage
                    : ((investor.amountInCash || 0) / totalOfferingSize) * 100

            const amountInCash = investor.amountInCash
                ? investor.amountInCash
                : ((investor.amountInPercentage || 0) / 100) * totalOfferingSize
            const result = acc[name] || {
                id,
                investorId,
                name,
                leo,
                putOption: false,
                totalAmountInCash: 0,
                totalAmountInPercentage: 0,
                levels: [],
            }

            result.putOption = result.putOption || isPutOptionFlag(putOption)

            result.totalAmountInCash += amountInCash
            result.totalAmountInPercentage += amountInPercentage
            result.levels.push({
                feeLevel,
                amountInPercentage,
                amountInCash,
                withoutRights: feeLevel === 0 ? isWithoutRightsFlag(investor.withoutRights) : null,
            })

            return {
                ...acc,
                [name]: {
                    ...acc[name],
                    ...result,
                },
            }
        },
        {} as Record<string, InvestorWithLevels>,
    )

    return Object.values(investorsWithLevels)
}

const sortInvestors = (
    investors: InvestorWithLevels[],
    orderBy: SortableKeys,
    order: 'asc' | 'desc',
    locale: string,
    splitFeeLevelZeroColumn: boolean,
) => {
    switch (orderBy) {
        case 'name':
            return investors.sort((a, b) => {
                if (order === 'asc') return a.name.localeCompare(b.name, locale)

                return b.name.localeCompare(a.name, locale)
            })
        case 'totalAmountInCash':
            return investors.sort((a, b) => {
                if (order === 'asc') return a.totalAmountInCash - b.totalAmountInCash

                return b.totalAmountInCash - a.totalAmountInCash
            })
        case 'default': {
            const maxFeeLevel = Math.max(
                0,
                ...investors.map((row) => Math.max(0, ...row.levels.map((level) => level.feeLevel))),
            )
            for (let feeLevelToSort = maxFeeLevel; feeLevelToSort >= 0; feeLevelToSort--) {
                if (feeLevelToSort === 0 && splitFeeLevelZeroColumn) {
                    for (const subCol of [1, 0]) {
                        investors = investors.sort((a, b) => {
                            const aLevel0 = getLevelForDisplayColumn(a.levels, subCol, true)
                            const bLevel0 = getLevelForDisplayColumn(b.levels, subCol, true)
                            if (aLevel0 && bLevel0) {
                                if (bLevel0.amountInCash === aLevel0.amountInCash) {
                                    return a.name.localeCompare(b.name)
                                }
                                return bLevel0.amountInCash - aLevel0.amountInCash
                            }
                            if (aLevel0 && !bLevel0) {
                                return -1
                            }
                            if (!aLevel0 && bLevel0) {
                                return 1
                            }
                            return 0
                        })
                    }
                    continue
                }
                investors = investors.sort((a, b) => {
                    const aLevel0 = splitFeeLevelZeroColumn
                        ? getLevelForDisplayColumn(a.levels, feeLevelToSort + 1, true)
                        : a.levels.find((l) => l.feeLevel === feeLevelToSort)
                    const bLevel0 = splitFeeLevelZeroColumn
                        ? getLevelForDisplayColumn(b.levels, feeLevelToSort + 1, true)
                        : b.levels.find((l) => l.feeLevel === feeLevelToSort)
                    if (aLevel0 && bLevel0) {
                        if (bLevel0.amountInCash === aLevel0.amountInCash) {
                            return a.name.localeCompare(b.name)
                        }
                        return bLevel0.amountInCash - aLevel0.amountInCash
                    }

                    if (aLevel0 && !bLevel0) {
                        return -1
                    }
                    if (!aLevel0 && bLevel0) {
                        return 1
                    }

                    return 0
                })
            }
            return investors
        }
        default: {
            if (!orderBy.startsWith('level_')) return investors
            const [, level] = orderBy.split('level_')

            return investors.sort((a, b) => {
                const getSumByLevel = ({ levels }: InvestorWithLevels) => {
                    if (splitFeeLevelZeroColumn) {
                        const lr = getLevelForDisplayColumn(levels, +level, true)
                        return lr?.amountInCash ?? 0
                    }
                    return levels.filter((l) => l.feeLevel === +level).reduce((acc, val) => acc + val.amountInCash, 0)
                }

                if (order === 'asc') return getSumByLevel(a) - getSumByLevel(b)

                return getSumByLevel(b) - getSumByLevel(a)
            })
        }
    }
}

function getLevelColumnHeader(
    displayIdx: number,
    maxFeeLevel: number,
    splitFeeLevelZeroColumn: boolean,
    t: (key: string) => string,
) {
    if (!splitFeeLevelZeroColumn) {
        return getLevelName(displayIdx, maxFeeLevel)
    }
    if (displayIdx === 0) {
        return getLevelName(0, maxFeeLevel)
    }
    if (displayIdx === 1) {
        return t('components.underwritersTable.subscriptionWithoutRights')
    }
    return getLevelName(displayIdx - 1, maxFeeLevel)
}

function formatFeeLevelForColumn(
    displayIdx: number,
    maxFeeLevel: number,
    splitFeeLevelZeroColumn: boolean,
    fees: Fee[],
    isUnit: boolean,
    locale: string,
) {
    if (splitFeeLevelZeroColumn) {
        if (displayIdx === 1) {
            return null
        }
        const levelArg = displayIdx === 0 ? 0 : displayIdx - 1
        return formatFeeLevel({ fees, isUnit }, levelArg, maxFeeLevel, locale)
    }
    return formatFeeLevel({ fees, isUnit }, displayIdx, maxFeeLevel, locale)
}

function UnderwritersAndGuarantorsTable({
    currency,
    fees,
    investors,
    isUnit,
    noGuarantors,
    totalOfferingSize,
    hideTotalColumns,
    splitFeeLevelZeroColumn = false,
}: {
    currency: string | null
    fees: Fee[]
    investors: InvestorBase[]
    isUnit: boolean
    noGuarantors?: boolean
    totalOfferingSize: number
    hideTotalColumns?: boolean
    /** Företrädesemission / IPO: visa TÅ utan rätter som egen kolumn till höger om Teckningsåtagare */
    splitFeeLevelZeroColumn?: boolean
}) {
    const { i18n, t } = useTranslation()
    const locale = i18n.language
    const [order, setOrder] = useState<'asc' | 'desc'>('desc')
    const [orderBy, setOrderBy] = useState<SortableKeys>('default')

    const investorsRows = sortInvestors(
        mapInvestorsToNamesAndLevels({ investors, totalOfferingSize }),
        orderBy,
        order,
        locale,
        splitFeeLevelZeroColumn,
    )

    const maxFeeLevel = Math.max(
        0,
        ...investorsRows.map((row) => Math.max(0, ...row.levels.map((level) => level.feeLevel))),
    )
    const levelColumnCount = getLevelColumnCount(maxFeeLevel, splitFeeLevelZeroColumn)

    const amountNotDefined = investorsRows.reduce((acc, investor) => {
        investor.levels.forEach((level) => {
            if (level.amountInCash === 0) {
                acc = true
            }
        })
        return acc
    }, false)

    const createSortHandler = (property: SortableKeys) => () => {
        let newOrder: 'asc' | 'desc'
        let isDefault: boolean

        if (property === 'name') {
            newOrder = orderBy === property && order === 'asc' ? 'desc' : 'asc'
            isDefault = orderBy === property && order === 'desc' // Third click will reset the order to default
        } else {
            newOrder = orderBy === property && order === 'desc' ? 'asc' : 'desc'
            isDefault = orderBy === property && order === 'asc' // Third click will reset the order to default
        }

        setOrder(newOrder)
        setOrderBy(isDefault ? 'default' : property)
    }

    return (
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <TableSortLabel
                                active={orderBy === 'name'}
                                direction={orderBy === 'name' ? order : 'asc'}
                                onClick={createSortHandler('name')}
                            >
                                {t('site.table.name')}
                            </TableSortLabel>
                        </TableCell>
                        {Array.from({ length: levelColumnCount }, (_, displayIdx) => (
                            <TableCell key={displayIdx}>
                                <TableSortLabel
                                    key={`level_` + displayIdx}
                                    active={orderBy === 'level_' + displayIdx}
                                    direction={orderBy === 'level_' + displayIdx ? order : 'asc'}
                                    onClick={createSortHandler(('level_' + displayIdx) as 'level_${number}')}
                                >
                                    {getLevelColumnHeader(displayIdx, maxFeeLevel, splitFeeLevelZeroColumn, t)}
                                </TableSortLabel>
                            </TableCell>
                        ))}
                        {!hideTotalColumns && [
                            <TableCell key='cash'>
                                <TableSortLabel
                                    active={orderBy === 'totalAmountInCash'}
                                    direction={orderBy === 'totalAmountInCash' ? order : 'asc'}
                                    onClick={createSortHandler('totalAmountInCash')}
                                >
                                    {t('components.underwritersTable.totalInCurrency', { currency })}
                                </TableSortLabel>
                            </TableCell>,
                            <TableCell key='percentage'>{t('components.underwritersTable.totalPercent')}</TableCell>,
                        ]}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {investorsRows.map((row, index) => (
                        <TableRow key={index}>
                            <TableCell>
                                <InvestorNameCell
                                    name={row.name}
                                    investorId={row.investorId}
                                    putOption={row.putOption}
                                />
                            </TableCell>
                            {Array.from({ length: levelColumnCount }, (_, displayIdx) => {
                                const levelRow = getLevelForDisplayColumn(
                                    row.levels,
                                    displayIdx,
                                    splitFeeLevelZeroColumn,
                                )

                                if (!levelRow) {
                                    return <TableCell key={displayIdx} />
                                }

                                return (
                                    <TableCell key={displayIdx}>
                                        {levelRow.amountInCash > 0
                                            ? formatMoney(levelRow.amountInCash, {
                                                  decimals: 0,
                                                  locale,
                                              })
                                            : '*'}
                                    </TableCell>
                                )
                            })}
                            {!hideTotalColumns && [
                                <TableCell key='cash'>
                                    {row.totalAmountInCash > 0
                                        ? formatMoney(row.totalAmountInCash, {
                                              decimals: 0,
                                              locale,
                                          })
                                        : null}
                                </TableCell>,
                                <TableCell key='percentage'>
                                    {row.totalAmountInPercentage > 0
                                        ? formatPercentage(row.totalAmountInPercentage, {
                                              decimals: 1,
                                              locale,
                                          })
                                        : null}
                                </TableCell>,
                            ]}
                        </TableRow>
                    ))}
                    <TableRow>
                        <TableCell>
                            <b>{t('site.table.total')}</b>
                        </TableCell>
                        {Array.from({ length: levelColumnCount }, (_, displayIdx) => (
                            <TableCell key={displayIdx}>
                                <b>
                                    {formatMoney(
                                        investorsRows.reduce((acc, row) => {
                                            const cell = getLevelForDisplayColumn(
                                                row.levels,
                                                displayIdx,
                                                splitFeeLevelZeroColumn,
                                            )
                                            return acc + (cell?.amountInCash ?? 0)
                                        }, 0),
                                        { decimals: 0, locale },
                                    )}
                                </b>
                            </TableCell>
                        ))}
                        {!hideTotalColumns && [
                            <TableCell key='cash'>
                                <b>
                                    {formatMoney(
                                        investorsRows.reduce((acc, row) => acc + row.totalAmountInCash, 0),
                                        { decimals: 0, locale },
                                    )}
                                </b>
                            </TableCell>,
                            <TableCell key='percentage'>
                                <b>
                                    {formatPercentage(
                                        investorsRows.reduce((acc, row) => acc + row.totalAmountInPercentage, 0),
                                        { decimals: 1, locale },
                                    )}
                                </b>
                            </TableCell>,
                        ]}
                    </TableRow>
                    {!noGuarantors && investorsRows.length > 0 && (
                        <TableRow>
                            <TableCell>
                                <Typography variant='caption'>{t('components.underwritersTable.compensationLevel')}</Typography>
                            </TableCell>
                            <TableCell />
                            {Array.from({ length: levelColumnCount - 1 + (hideTotalColumns ? 0 : 2) }, (_, i) => i).map(
                                (displayIdx) => (
                                    <TableCell key={displayIdx}>
                                        <Typography variant='caption' style={{ whiteSpace: 'pre-line' }}>
                                            {formatFeeLevelForColumn(
                                                displayIdx,
                                                maxFeeLevel,
                                                splitFeeLevelZeroColumn,
                                                fees,
                                                isUnit,
                                                locale,
                                            )}
                                        </Typography>
                                    </TableCell>
                                ),
                            )}
                        </TableRow>
                    )}
                    {amountNotDefined ? (
                        <TableRow>
                            <TableCell colSpan={1 + levelColumnCount + (hideTotalColumns ? 0 : 2)}>
                                <Typography variant='caption'>
                                    *<i>{t('components.underwritersTable.amountDisclaimer')}</i>
                                </Typography>
                            </TableCell>
                        </TableRow>
                    ) : null}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

export default UnderwritersAndGuarantorsTable
